import { CalculationContext, FieldsKey } from "./types"

import { ChangedPlanRecord } from "../../types"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { calculateFinancials } from "./financials"
import { calculateFormula, calculateFormulas } from "./formulas"
import { calculateMarkups } from "./markups"
import { calculateSystemFields } from "./system"
import {
  calculateCpms,
  calculateCpps,
  calculateGrps,
  calculateImps,
} from "./metrics"
import { set } from "lodash"
import { getMediaField, getMediaFieldValue, systemFormulas } from "./utils"

export const DEFAULT_FIELDS_KEY: FieldsKey = "planningMediaFields"
const DEFAULT_BUDGET_PLANNING: "GROSS_COST" | "NET_COST" | "BILLABLE" =
  "BILLABLE"

export const calculationContext: CalculationContext = {
  media: undefined,
  baseRate: undefined,
  loadings: undefined,
  applyLoadings: false,
  mcCommissions: undefined,
  acCommissions: undefined,
  fees: undefined,
  billingTypes: undefined,
  taxes: undefined,
  fieldsKey: DEFAULT_FIELDS_KEY,
  budgetPlanning: DEFAULT_BUDGET_PLANNING,
  timeZone: "Etc/UTC",
  populations: undefined,
  metricsConst: undefined,
}

export function calculate(
  li: ChangedPlanRecord,
  context: Partial<CalculationContext> & { mediaConfig: MediaType[] },
  precision: number,
) {
  // initialize context
  const { mediaConfig, populations, metricsConst, ...rest } = context
  // find and apply media
  const mediaId = li?.ancestorMediaId
  calculationContext.media = mediaConfig.find((mc) => mc.id === mediaId)
  // apply the rest
  Object.keys(rest).forEach((k) =>
    set(calculationContext, k, rest[k as keyof typeof rest]),
  )
  // applyLoadings must always be false at start
  calculationContext.applyLoadings = false

  // calculation steps
  const system = calculateSystemFields(li)
  const withImpsAndGrps = calculateGrps(
    calculateImps(system, populations, metricsConst),
    precision,
    populations,
    metricsConst,
  )
  const formulas = calculateFormulas(withImpsAndGrps, precision)
  const adjusted = adjustFormulas(formulas, precision)

  // if a line is packaged stop here
  if (li.packaged) return adjusted

  const financials = calculateFinancials(adjusted, precision)
  const withCppsAndCpms = calculateCpms(
    calculateCpps(financials, precision),
    precision,
  )
  const markups = calculateMarkups(withCppsAndCpms)

  return markups
}

const adjustFormulas = (li: ChangedPlanRecord, precision: number) => {
  const cc = calculationContext
  const media = cc.media
  if (!media) return li

  const { grossCostCppFormula } = systemFormulas()
  const grossCostField = getMediaField("grossCost")
  const negotiationTypeField = getMediaField("negotiationType")
  const negotiationTypeValue = getMediaFieldValue<Record<string, string>>(
    li?.planningMediaFields,
    negotiationTypeField,
  )
  const hasCppNt =
    !!negotiationTypeField && negotiationTypeValue?.negotiationType === "CPP"

  // return calculated
  const overridden =
    hasCppNt && !!grossCostField
      ? calculateFormula(
          media.mediaFields,
          li[cc.fieldsKey],
          {
            ...grossCostField,
            formulaSet: grossCostCppFormula,
          },
          precision,
        )
      : li[cc.fieldsKey]

  if (!overridden) return li

  return { ...li, [cc.fieldsKey]: overridden }
}
